import React from 'react'
import { Box, Button, Stack, TableContainer } from '@mui/material'
import PageSection from '../../Constants&Theme/PageSection'
import { stackGap } from '../../Constants&Theme/Spacing'
import { DataGridPro } from '@mui/x-data-grid-pro'
import { Clear, Settings } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import ModalClear from './Modals/ModalClear'
import { formatValueDateTime } from '../../Functions/FormatValues'
import Step3Apply from './Steps/Step3Apply'
import Step2Select from './Steps/Step2Select'
import Step1Scan from './Steps/Step1Scan'
import Step4Process from './Steps/Step4Process'
import Step5Generate from './Steps/Step5Generate'
import { useReaderContext } from '../../Contexts/Modules/ReaderModule/ReaderModule'
import { getCodeSetLabelLookup } from '../../Functions/CodeSetFns'
import { useUserContext } from '../../Contexts/UserConfig'
import clsx from 'clsx'
import { useHoldingContext } from '../../Contexts/CurrentHolding'
import ModalModeSwitch from './Modals/ModalModeSwitch'

function ReaderMain() {
  const navigate = useNavigate()
  const { currentHolding } = useHoldingContext()
  const { loginConfig } = useUserContext()
  const {
    setContainerSelectionModel,
    barcodes,
    setVesselSelectionModel,
    vesselSelectionModel,
    containerSelectionModel,
    mutateBarcodes,
    setBarcodes,
  } = useReaderContext()

  const dgContainerSX = {
    width: 1,
    '& .container-holding-cell.invalid': {
      color: 'red',
      fontWeight: '600',
    },
  }
  return (
    <Box
      maxWidth={'lg'}
      width={1}
    >
      <PageSection
        title={'Code Reader'}
        rightDecorator={
          <Stack
            direction={'row'}
            gap={stackGap.row}
          >
            <ModalModeSwitch />
            {loginConfig?.Modules?.ShowReaderSettings && (
              <Button
                variant={'outlined'}
                onClick={() => navigate('settings')}
                startIcon={<Settings />}
              >
                Settings
              </Button>
            )}
          </Stack>
        }
        leftDecorator={
          <Stack
            direction={'row'}
            gap={stackGap.row}
          >
            <ModalClear />
            <Button
              variant={'outlined'}
              startIcon={<Clear />}
              color={'error'}
              onClick={() => {
                setBarcodes({})
                mutateBarcodes.reset()
              }}
            >
              Below
            </Button>
          </Stack>
        }
      >
        <Stack gap={stackGap.col}>
          <Stack
            direction={'row'}
            width={1}
            gap={stackGap.row}
          >
            <Step1Scan />
            <Step2Select />
            <Step3Apply />
            <Step4Process />
            <Step5Generate />
          </Stack>
          <PageSection
            title={'Containers'}
            headingLevel={'h5'}
          >
            <TableContainer sx={dgContainerSX}>
              <DataGridPro
                autoHeight={true}
                isRowSelectable={({ row }) =>
                  row.Zone !== '- Not Found -' &&
                  row.HoldingID === currentHolding.value
                }
                rows={[
                  ...(mutateBarcodes?.data?.containers?.not_found ?? []).map(
                    (id: any) => ({
                      id,
                      ContainerID: id,
                      Zone: '- Not Found -',
                      HoldingID: '- Not Found -',
                      Timestamp: barcodes[id] ?? '',
                    }),
                  ),
                  ...Object.values(
                    mutateBarcodes?.data?.containers?.found ?? {},
                  ).map((container: any) => ({
                    id: container.ContainerID,
                    ContainerID: container.ContainerID,
                    Zone: container?.ZoneName,
                    HoldingID: container?.HoldingID,
                    Rack: container?.RackName,
                    Timestamp: barcodes[container.ContainerID] ?? '',
                  })),
                ]}
                initialState={{
                  sorting: {
                    sortModel: [{ field: 'Timestamp', sort: 'asc' }],
                  },
                }}
                columns={[
                  {
                    field: 'ContainerID',
                    headerName: 'Container ID',
                    flex: 1,
                  },
                  {
                    field: 'Timestamp',
                    headerName: 'Scan Date',
                    flex: 1,
                    valueFormatter: (value: any) => formatValueDateTime(value),
                  },
                  {
                    field: 'HoldingID',
                    headerName: 'Holding',
                    valueFormatter: (value) => {
                      if (value === '- Not Found -') {
                        return value
                      } else {
                        return getCodeSetLabelLookup(
                          loginConfig,
                          ['KV-Holdings'],
                          value,
                        )
                      }
                    },
                    cellClassName: ({ value }) =>
                      clsx('container-holding-cell', {
                        invalid: value !== currentHolding.value,
                      }),
                    flex: 1,
                  },
                  {
                    field: 'Zone',
                    headerName: 'Zone',
                    flex: 1,
                    cellClassName: ({ value }) =>
                      clsx('container-holding-cell', {
                        invalid: value === '- Not Found -',
                      }),
                  },
                  {
                    field: 'Rack',
                    headerName: 'Rack',
                    flex: 1,
                  },
                ]}
                onRowSelectionModelChange={(newRowSelectionModel) => {
                  setContainerSelectionModel(newRowSelectionModel)
                }}
                rowSelectionModel={containerSelectionModel}
                loading={mutateBarcodes.isLoading}
                checkboxSelection={true}
              />
            </TableContainer>
          </PageSection>
          <PageSection
            title={'Vessels'}
            headingLevel={'h5'}
          >
            <TableContainer sx={dgContainerSX}>
              <DataGridPro
                autoHeight={true}
                isRowSelectable={({ row }) => row.HoldingID !== -1}
                rows={[
                  ...(mutateBarcodes?.data?.vessels?.not_found ?? []).map(
                    (id: any) => ({
                      id,
                      VesselID: id,
                      HoldingID: -1,
                      Timestamp: barcodes[id] ?? '',
                      Status: -1,
                    }),
                  ),
                  ...Object.values(
                    mutateBarcodes?.data?.vessels?.found ?? {},
                  ).map((vessel: any) => ({
                    id: vessel.VesselID,
                    VesselID: vessel.VesselID,
                    HoldingID: vessel?.HoldingID,
                    Type: vessel?.VesselType,
                    Timestamp: barcodes[vessel.VesselID] ?? '',
                    Status: vessel?.Status,
                  })),
                ]}
                initialState={{
                  sorting: {
                    sortModel: [{ field: 'Timestamp', sort: 'asc' }],
                  },
                }}
                columns={[
                  {
                    field: 'VesselID',
                    headerName: 'Vessel ID',
                    flex: 1,
                  },
                  {
                    field: 'Timestamp',
                    headerName: 'Scan Date',
                    flex: 1,
                    valueFormatter: (value: any) => formatValueDateTime(value),
                  },
                  {
                    field: 'HoldingID',
                    headerName: 'Holding',
                    flex: 1,
                    cellClassName: ({ value }) =>
                      clsx('container-holding-cell', {
                        invalid: value !== currentHolding.value,
                      }),
                    valueFormatter: (value) => {
                      if (value === -1) {
                        return '- Not Found -'
                      } else {
                        return getCodeSetLabelLookup(
                          loginConfig,
                          ['KV-Holdings'],
                          value,
                        )
                      }
                    },
                  },
                  {
                    field: 'Type',
                    headerName: 'Type',
                    flex: 1,
                    valueFormatter: (value: any) =>
                      getCodeSetLabelLookup(
                        loginConfig,
                        ['VesselTypeID'],
                        value,
                      ),
                  },
                  {
                    field: 'Status',
                    headerName: 'Status',
                    flex: 1,
                    valueFormatter: (value: any) => {
                      if (value === -1) {
                        return '- Unknown -'
                      } else {
                        return getCodeSetLabelLookup(
                          loginConfig,
                          ['VesselStatusID'],
                          value,
                        )
                      }
                    },
                    cellClassName: ({ value }) =>
                      clsx('container-holding-cell', {
                        invalid: [-1, 'DES'].includes(value),
                      }),
                  },
                ]}
                onRowSelectionModelChange={(newRowSelectionModel) => {
                  setVesselSelectionModel(newRowSelectionModel)
                }}
                rowSelectionModel={vesselSelectionModel}
                loading={mutateBarcodes.isLoading}
                checkboxSelection={true}
              />
            </TableContainer>
          </PageSection>
        </Stack>
      </PageSection>
    </Box>
  )
}

export default ReaderMain
